/**
 * GuidedTour - First-time user onboarding tour
 */
import { ReactElement, useState, useEffect, useMemo } from 'react'
import { Tour } from 'antd'
import type { TourProps } from 'antd'
import localStorage from 'lib/local-storage'
import { isDemoMode } from 'lib/is-demo'

const TOUR_COMPLETED_KEY = 'virtuallog_tour_completed'

const tourTarget = (selector: string) => () =>
  document.querySelector(selector) as HTMLElement

const BASE_STEPS: TourProps['steps'] = [
  {
    title: 'Navigation',
    description:
      'Use the sidebar to switch between Search ' +
      'and Settings pages. Click the collapse ' +
      'button to save space.',
    target: tourTarget('[data-tour="sidebar-nav"]'),
    placement: 'right',
  },
  {
    title: 'Time Range & Search',
    description:
      'Select a predefined time range or choose ' +
      'a custom date range. Click Search to ' +
      'refresh all data.',
    target: tourTarget('[data-tour="search-bar"]'),
    placement: 'bottom',
  },
  {
    title: 'Attributes',
    description:
      'Browse your log attributes in a tree view. ' +
      'Use the filter input to quickly find ' +
      'specific attributes. Click an attribute ' +
      'to see its values.',
    target: tourTarget('[data-tour="attributes-panel"]'),
    placement: 'right',
  },
  {
    title: 'Attribute Values',
    description:
      'View values for the selected attribute, ' +
      'sorted by frequency. Click a value to ' +
      'add it as a filter.',
    target: tourTarget('[data-tour="values-panel"]'),
    placement: 'right',
  },
  {
    title: 'Events Chart',
    description:
      'Visualize log volume over time. Click a ' +
      'bar to zoom into that interval, or drag ' +
      'to select a custom range. Use the zoom ' +
      'buttons for quick navigation.',
    target: tourTarget('[data-tour="logs-chart"]'),
    placement: 'bottom',
  },
  {
    title: 'Logs Table',
    description:
      'View your log entries with timestamp, ' +
      'level, and message. Click a row to expand ' +
      'and see the full JSON. Use Load More at ' +
      'the bottom for pagination.',
    target: tourTarget('[data-tour="logs-table"]'),
    placement: 'top',
  },
]

const DEMO_FILTER_STEP:
  TourProps['steps'][number] = {
    title: 'Browser Session Filter',
    description:
      'This filter shows only your own logs. ' +
      'Remove it to see logs from all demo users.',
    target: tourTarget(
      '[data-tour="applied-filters"]'
    ),
    placement: 'bottom',
  }

const GuidedTour = (): ReactElement | null => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const isCompleted = localStorage.get(TOUR_COMPLETED_KEY)
    if (isCompleted) return

    const timer = setTimeout(() => setOpen(true), 500)
    return () => clearTimeout(timer)
  }, [])

  const handleClose = (): void => {
    localStorage.set(TOUR_COMPLETED_KEY, 'completed')
    setOpen(false)
  }

  const steps = useMemo(() => {
    if (!isDemoMode) return BASE_STEPS
    return [...BASE_STEPS, DEMO_FILTER_STEP]
  }, [])

  if (!open) return null

  return (
    <Tour
      open={open}
      onClose={handleClose}
      onFinish={handleClose}
      steps={steps}
      type="primary"
    />
  )
}

export default GuidedTour
